import { Button, Modal } from '@/components/ui';
import { useEvidencias } from '@/hooks/useEvidencias';
import { UploadFolderResponse } from '@/services/evidencias.service';
import { CheckCircle, File, Folder, Plus, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface NuevaEvidenciaModalProps {
  isOpen: boolean;
  onClose: () => void;
  nit: string;
  /** Asesoría a la que pertenece la evidencia — nunca "la asesoría activa" de la empresa. */
  asesoriaId: number;
  onSuccess?: () => void;
  disabled?: boolean;
  disabledMessage?: string;
  /** When set, pre-fills the evidence name (used to re-upload a rejected evidence). */
  prefillNombre?: string;
  /** When set, the new evidence is linked as a replacement of this rejected evidence. */
  reemplazaEvidenciaId?: number;
}

type UploadMode = 'archivo' | 'carpeta';

interface FolderResult {
  total: number;
  exitosos: number;
  fallidos: number;
  resultados: { nombre: string; ok: boolean; error?: string }[];
}

export function NuevaEvidenciaModal({
  isOpen,
  onClose,
  nit,
  asesoriaId,
  onSuccess,
  disabled = false,
  disabledMessage = 'Esta asesoria esta en revisión y no puede modificarse.',
  prefillNombre,
  reemplazaEvidenciaId,
}: NuevaEvidenciaModalProps) {
  const { uploadEvidencia, uploadFolder, isUploading } = useEvidencias(nit, asesoriaId, () => {
    onSuccess?.();
  });

  // ── Archivo individual ──
  const [mode, setMode] = useState<UploadMode>('archivo');
  const [nombreEvidencia, setNombreEvidencia] = useState('');
  const [descripcionEvidencia, setDescripcionEvidencia] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);

  // ── Carpeta ──
  const [folderFiles, setFolderFiles] = useState<File[]>([]);
  const [folderResult, setFolderResult] = useState<FolderResult | null>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setNombreEvidencia('');
    setDescripcionEvidencia('');
    setArchivo(null);
    setFolderFiles([]);
    setFolderResult(null);
  };

  const handleClose = () => {
    if (isUploading) return;
    resetForm();
    setMode('archivo');
    onClose();
  };

  // Seed the name when opened to re-upload a rejected evidence.
  useEffect(() => {
    if (isOpen && prefillNombre) {
      setMode('archivo');
      setNombreEvidencia(prefillNombre);
    }
  }, [isOpen, prefillNombre]);

  // ─── Subida de archivo individual ───────────────────────────────────────────
  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled || !archivo || !nombreEvidencia || isUploading) return;
    const formData = new FormData();
    formData.append('file', archivo);
    formData.append('name', nombreEvidencia);
    if (descripcionEvidencia.trim()) formData.append('description', descripcionEvidencia);
    if (reemplazaEvidenciaId !== undefined) formData.append('reemplazaEvidenciaId', String(reemplazaEvidenciaId));
    try {
      await uploadEvidencia(formData);
      handleClose();
    } catch (err) {
      alert('Error al subir evidencia: ' + (err instanceof Error ? err.message : 'Error desconocido'));
    }
  };

  // ─── Subida de carpeta ───────────────────────────────────────────────────────
  const handleFolderSubmit = async () => {
    if (disabled || folderFiles.length === 0 || isUploading) return;
    setFolderResult(null);
    try {
      const result: UploadFolderResponse = await uploadFolder(folderFiles);
      setFolderResult(result);
      if (result.fallidos === 0) {
        // Todo OK → cerrar tras un momento breve
        setTimeout(handleClose, 1200);
      }
    } catch (err) {
      alert('Error al subir carpeta: ' + (err instanceof Error ? err.message : 'Error desconocido'));
    }
  };

  // ─── Helpers de presentación ────────────────────────────────────────────────
  const folderName = folderFiles.length > 0 ? folderFiles[0].webkitRelativePath.split('/')[0] : '';

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Añadir Evidencia">
      {disabled && (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">{disabledMessage}</p>
      )}

      {/* ── Tabs ── */}
      <div className="flex border-b border-gray-200 mb-5">
        <TabButton
          active={mode === 'archivo'}
          onClick={() => {
            setMode('archivo');
            resetForm();
          }}
          icon={<File className="w-4 h-4" />}
          label="Archivo individual"
          disabled={isUploading}
        />
        <TabButton
          active={mode === 'carpeta'}
          onClick={() => {
            setMode('carpeta');
            resetForm();
          }}
          icon={<Folder className="w-4 h-4" />}
          label="Carpeta completa"
          disabled={isUploading}
        />
      </div>

      {/* ══ MODO: ARCHIVO INDIVIDUAL ══════════════════════════════════════════ */}
      {mode === 'archivo' && (
        <form className="space-y-4" onSubmit={handleSingleSubmit}>
          <div>
            <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de la Evidencia <span className="text-red-500">*</span>
            </label>
            <input
              id="nombre"
              type="text"
              value={nombreEvidencia}
              onChange={(e) => setNombreEvidencia(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ej: Matriz del SIG"
              required
              disabled={isUploading || disabled}
            />
          </div>
          <div>
            <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción (opcional)
            </label>
            <textarea
              id="descripcion"
              value={descripcionEvidencia}
              onChange={(e) => setDescripcionEvidencia(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe brevemente la evidencia..."
              rows={3}
              disabled={isUploading || disabled}
            />
          </div>
          <div>
            <label
              htmlFor="archivo"
              className={`flex flex-col items-center justify-center w-full h-36
                border-2 border-dashed rounded-lg cursor-pointer transition
                ${archivo ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}
                ${isUploading || disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex flex-col items-center justify-center text-center px-4">
                <Plus className="w-8 h-8 text-gray-400 mb-2" />
                {!archivo ? (
                  <>
                    <p className="text-sm text-gray-600">
                      Haz clic para subir un archivo <span className="text-red-500">*</span>
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, imágenes (máx. 15 MB)</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-medium text-gray-700 truncate max-w-full text-wrap">{archivo.name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(archivo.size / (1024 * 1024)).toLocaleString('es-CO', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      MB
                    </p>
                  </>
                )}
              </div>
              <input
                id="archivo"
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 15 * 1024 * 1024) {
                    alert('El archivo no puede superar los 15 MB');
                    e.target.value = '';
                    return;
                  }
                  setArchivo(file);
                  if (nombreEvidencia.trim() === '') setNombreEvidencia(file.name.replace(/\.[^/.]+$/, ''));
                }}
                required
                disabled={isUploading || disabled}
              />
            </label>
            {archivo && (
              <button
                type="button"
                onClick={() => {
                  setArchivo(null);
                  setNombreEvidencia('');
                }}
                className="mt-2 text-xs text-red-600 hover:underline"
                disabled={isUploading || disabled}
              >
                Quitar archivo
              </button>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isUploading || !archivo || !nombreEvidencia || disabled}>
              {isUploading ? 'Subiendo...' : 'Añadir'}
            </Button>
          </div>
        </form>
      )}

      {/* ══ MODO: CARPETA ════════════════════════════════════════════════════ */}
      {mode === 'carpeta' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Selecciona una carpeta y todos sus archivos (incluidas subcarpetas) se subirán a Google Drive conservando la estructura de
            directorios.
          </p>

          {/* Drop-zone / selector de carpeta */}
          {folderFiles.length === 0 ? (
            <label
              htmlFor="folder-input"
              className={`flex flex-col items-center justify-center w-full h-40
                border-2 border-dashed rounded-lg cursor-pointer transition
                border-gray-300 hover:border-blue-400 hover:bg-gray-50
                ${disabled || isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <Folder className="w-10 h-10 text-gray-400 mb-2" />
              <p className="text-sm text-gray-600 font-medium">Seleccionar carpeta</p>
              <p className="text-xs text-gray-400 mt-1">Se subirán todos los archivos incluyendo subcarpetas</p>
              <input
                id="folder-input"
                ref={folderInputRef}
                type="file"
                className="hidden"
                // @ts-ignore — atributo no estándar, soportado por todos los browsers modernos
                webkitdirectory=""
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files ?? []);
                  setFolderFiles(files);
                  setFolderResult(null);
                }}
                disabled={disabled || isUploading}
              />
            </label>
          ) : (
            /* Vista previa + resultado */
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Folder className="w-5 h-5 text-blue-500" />
                  <span className="font-medium text-gray-800 text-sm">{folderName}</span>
                  <span className="text-xs text-gray-500">({folderFiles.length} archivos)</span>
                </div>
                {!isUploading && !folderResult && (
                  <button
                    type="button"
                    onClick={() => {
                      setFolderFiles([]);
                      setFolderResult(null);
                    }}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Cambiar carpeta
                  </button>
                )}
              </div>

              {/* Lista de archivos a subir (preview) */}
              {!folderResult && (
                <ul className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                  {folderFiles.map((f, i) => (
                    <li key={i} className="px-4 py-2 flex items-center gap-2 text-xs text-gray-600">
                      <File className="w-3 h-3 shrink-0 text-gray-400" />
                      <span className="truncate">{f.webkitRelativePath || f.name}</span>
                      <span className="ml-auto shrink-0 text-gray-400">{(f.size / 1024).toFixed(0)} KB</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Resultado tras subida */}
              {folderResult && (
                <div className="p-4 space-y-3">
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-700 font-medium flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" /> {folderResult.exitosos} exitosos
                    </span>
                    {folderResult.fallidos > 0 && (
                      <span className="text-red-600 font-medium flex items-center gap-1">
                        <XCircle className="w-4 h-4" /> {folderResult.fallidos} fallidos
                      </span>
                    )}
                  </div>
                  {folderResult.fallidos > 0 && (
                    <>
                      <p className="text-xs text-red-600 font-medium">Archivos con error (reinténtalos individualmente):</p>
                      <ul className="space-y-1 max-h-36 overflow-y-auto">
                        {folderResult.resultados
                          .filter((r) => !r.ok)
                          .map((r, i) => (
                            <li key={i} className="flex items-center gap-2 text-xs text-red-700 bg-red-50 rounded px-2 py-1">
                              <XCircle className="w-3 h-3 shrink-0" />
                              <span className="truncate">{r.nombre}</span>
                            </li>
                          ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
              {folderResult ? 'Cerrar' : 'Cancelar'}
            </Button>
            {!folderResult && (
              <Button onClick={handleFolderSubmit} disabled={isUploading || folderFiles.length === 0 || disabled}>
                {isUploading
                  ? `Subiendo ${folderFiles.length} archivo${folderFiles.length !== 1 ? 's' : ''}...`
                  : `Subir ${folderFiles.length > 0 ? folderFiles.length + ' archivos' : 'carpeta'}`}
              </Button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Sub-componente: botón de tab ────────────────────────────────────────────
function TabButton({
  active,
  onClick,
  icon,
  label,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors
        ${active ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      {icon}
      {label}
    </button>
  );
}
